import { useMutation } from "@apollo/client";
import { Box, FormControl, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { useAlerts } from "components/Alerts/AlertProvider";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { Dialog } from "components/Dialog";
import { RecipientType } from "encamp-shared/src/constants/submissions";
import { getGeneratorStatusChangeSubmissionMethod } from "encamp-shared/src/submissions";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import { SubmissionMethod } from "generated-graphql/graphql";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { toUtcMidnightIsoFromDateLike } from "util/dates";

/** A facility selected on the dashboard to create a notification for. */
export type GeneratorStatusChangeFacility = {
  facilityId: string;
  tenantId: string;
  facilityName: string;
  organization: string;
  state: string | null;
  /** The exceedance period (ISO date) — drives the title and reporting period. */
  periodStart: string;
};

/** Sentinel for "auto-assign the method from each facility's state". */
const AUTO_METHOD = "AUTO";

/** Amazon standard turnaround — the default Jess wants pre-filled. */
const DEFAULT_DUE_DATE_DAYS = 30;

export const CREATE_GENERATOR_STATUS_CHANGE_SUBMISSIONS_MUTATION = gql(`
  mutation CreateGeneratorStatusChangeSubmissions(
    $input: CreateGeneratorStatusChangeSubmissionsInput!
  ) {
    createGeneratorStatusChangeSubmissions(input: $input) {
      succeeded
      created
      skipped
      failed
    }
  }
`);

type CreateGeneratorStatusChangeSubmissionsDialogProps = {
  open: boolean;
  onClose: () => void;
  facilities: GeneratorStatusChangeFacility[];
  /** Called after a successful create so the caller can clear its selection. */
  onSuccess: () => void;
};

type PreviewRow = {
  id: string;
  organization: string;
  facility: string;
  state: string;
  method: string;
};

export function CreateGeneratorStatusChangeSubmissionsDialog({
  open,
  onClose,
  facilities,
  onSuccess,
}: CreateGeneratorStatusChangeSubmissionsDialogProps) {
  const alerts = useAlerts();

  const [dueDate, setDueDate] = useState<DateTime | null>(() =>
    DateTime.now().plus({ days: DEFAULT_DUE_DATE_DAYS })
  );
  const [recipient, setRecipient] = useState<string>(RecipientType.REGULATORY_AGENCY);
  const [methodOverride, setMethodOverride] = useState<SubmissionMethod | typeof AUTO_METHOD>(
    AUTO_METHOD
  );

  const [createSubmissions, { loading }] = useMutation(
    CREATE_GENERATOR_STATUS_CHANGE_SUBMISSIONS_MUTATION,
    {
      refetchQueries: ["StaffGeneratorStatusChanges"],
      awaitRefetchQueries: true,
    }
  );

  const resolveMethod = useMemo(
    () => (state: string | null) =>
      methodOverride === AUTO_METHOD
        ? getGeneratorStatusChangeSubmissionMethod(state)
        : methodOverride,
    [methodOverride]
  );

  const previewRows: PreviewRow[] = useMemo(
    () =>
      facilities.map((f) => ({
        id: f.facilityId,
        organization: f.organization,
        facility: f.facilityName,
        state: f.state ?? "—",
        method: prettyPrintEnumValue(resolveMethod(f.state)),
      })),
    [facilities, resolveMethod]
  );

  const columns: CustomGridColDef<PreviewRow>[] = useMemo(
    () => [
      { field: "organization", headerName: "Organization", flex: 1, minWidth: 140 },
      { field: "facility", headerName: "Facility", flex: 1, minWidth: 140 },
      { field: "state", headerName: "State", width: 80 },
      { field: "method", headerName: "Submission Method", flex: 1, minWidth: 180 },
    ],
    []
  );

  const handleSubmit = async () => {
    if (!dueDate) {
      alerts.error("Please select a due date");
      return;
    }
    const dueDateIso = toUtcMidnightIsoFromDateLike(dueDate);
    if (!dueDateIso) {
      alerts.error("Invalid due date selected");
      return;
    }
    if (facilities.length === 0) {
      alerts.error("No facilities selected");
      return;
    }

    try {
      const result = await createSubmissions({
        variables: {
          input: {
            facilities: facilities.map((f) => ({
              facilityId: f.facilityId,
              periodStart: f.periodStart,
            })),
            dueDate: dueDateIso,
            recipient: recipient || undefined,
            submissionMethod: methodOverride === AUTO_METHOD ? undefined : methodOverride,
          },
        },
      });

      const data = result.data?.createGeneratorStatusChangeSubmissions;
      if (!data) {
        alerts.error("Failed to create submissions");
        return;
      }

      const { created, skipped, failed } = data;
      const skippedNote =
        skipped > 0 ? ` ${skipped} skipped (already had an open notification).` : "";

      if (failed > 0) {
        alerts.warning(`Created ${created} notification(s), but ${failed} failed.${skippedNote}`);
      } else {
        alerts.success(`Created ${created} notification(s).${skippedNote}`);
      }
      onSuccess();
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      alerts.error(`Failed to create submissions: ${message}`);
    }
  };

  const count = facilities.length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      slotProps={{
        paper: { sx: { width: 820, maxWidth: "90vw", maxHeight: "90vh" } },
      }}
      title={`Create ${count} Generator Status Change Notification${count === 1 ? "" : "s"}`}
      description="One submission is created per facility. The type is always a Generator Status Change Notification; the method is auto-assigned by state unless overridden below."
      primaryActionButtonProps={{
        onClick: handleSubmit,
        loading,
        loadingText: "Creating...",
        hideStartIcon: true,
        saveText: `Create ${count} Notification${count === 1 ? "" : "s"}`,
        disabled: loading || !dueDate || count === 0,
      }}
      cancelButtonProps={{ disabled: loading }}
    >
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <DatePicker
          label="Due Date *"
          value={dueDate}
          onChange={setDueDate}
          slotProps={{ textField: { size: "small", sx: { width: 200 } } }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Recipient</InputLabel>
          <Select
            value={recipient}
            label="Recipient"
            onChange={(e) => setRecipient(e.target.value)}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            <MenuItem value={RecipientType.REGULATORY_AGENCY}>Regulatory Agency</MenuItem>
            <MenuItem value={RecipientType.THIRD_PARTY}>Third Party</MenuItem>
            <MenuItem value={RecipientType.INTERNAL}>Internal</MenuItem>
            <MenuItem value={RecipientType.OTHER}>Other</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ flex: 1, minWidth: 220 }}>
          <InputLabel>Submission Method</InputLabel>
          <Select<SubmissionMethod | typeof AUTO_METHOD>
            value={methodOverride}
            label="Submission Method"
            onChange={(e) => setMethodOverride(e.target.value)}
          >
            <MenuItem value={AUTO_METHOD}>Auto (by state)</MenuItem>
            {Object.values(SubmissionMethod).map((value) => (
              <MenuItem key={value} value={value}>
                {prettyPrintEnumValue(value)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {count} facilit{count === 1 ? "y" : "ies"} — one notification each
        </Typography>
        <DataGrid
          rows={previewRows}
          columns={columns}
          disableRowSelectionOnClick
          pagination
          paginationMode="client"
          pageSizeOptions={[5, 10, 25]}
          hideFooterPagination={false}
          initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
          sx={{ maxHeight: 360 }}
        />
      </Box>
    </Dialog>
  );
}
